import React, { memo } from "react";
import Image, { StaticImageData } from "next/image";
import { Typography } from "@/components/ui/typography";
import { cn } from "@/lib/utils";
import Link from "next/link";
import Icon from "@/components/ui/Icon";
import ArrowLink from "@/components/ui/arrow-link";

export type IndustryCardProps = {
  title: string;
  description?: string;
  imageUrl: string | StaticImageData;
  link?: string;
  linkLabel?: string;
  isStatic?: boolean;
} & React.ComponentProps<"div">;

const IndustryCard = ({
  title,
  imageUrl,
  description,
  link = "#",
  linkLabel = "Learn more",
  isStatic,
  className,
}: IndustryCardProps) => {
  return (
    <div
      className={cn(
        "flex flex-col border bg-white border-neutral-200 rounded-3xl overflow-hidden lg:rounded-2xl lg:min-h-[220]",
        { "border-[#2F80ED]": isStatic, "lg:flex-row": !isStatic },
        className
      )}
    >
      {isStatic ? (
        <Image
          src={imageUrl}
          alt=""
          width={82}
          height={82}
          className="opacity-80 m-5 shrink-0"
        />
      ) : (
        <div className="flex w-full relative overflow-hidden rounded-3xl shrink-0 aspect-[1/0.69] lg:max-w-[232] lg:rounded-none lg:rounded-tl-2xl lg:rounded-bl-2xl">
          <Image
            alt={title}
            src={imageUrl}
            fill
            sizes="(max-width: 1024px) 100vw, 232px"
            className="w-full h-full object-cover"
            loading="lazy"
          />
        </div>
      )}
      <div
        className={cn("flex flex-col p-5 min-w-0", {
          "flex-auto": !isStatic,
          "lg:flex-auto": isStatic,
        })}
      >
        <div
          className={cn("grid grid-cols-1 place-content-start gap-2 lg:gap-3", {
            "flex-auto": !isStatic,
            "mt-auto pt-0.5": isStatic,
          })}
        >
          <Typography
            variant="titleMd"
            asChild
            className="lg:font-semibold lg:leading-[150%]"
          >
            <h4>{title}</h4>
          </Typography>
          {!!description && (
            <Typography
              variant="labelMd"
              asChild
              className="text-gray-600 line-clamp-3 leading-[160%] lg:line-clamp-none lg:text-sm lg:leading-[150%]"
            >
              <p>{description}</p>
            </Typography>
          )}
        </div>
        <div className={cn("mt-3 flex", { "lg:mt-[9]": !isStatic })}>
          <ArrowLink 
            href={link}
            aria-label={`${linkLabel} about ${title}`}
            title={`${linkLabel} about ${title}`}
            className="focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 rounded"
          >
            {linkLabel}
          </ArrowLink>
        </div>
      </div>
    </div>
  );
};

export default memo(IndustryCard);
